// src/utils/utils.ts

import { TreeViewBaseItem } from '@mui/x-tree-view';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { t } from 'i18next';
import { jsPDF } from 'jspdf';
import { uniq } from 'lodash';
import { RQBJsonLogic } from 'react-querybuilder';
import { ISelectItem } from '../components/common/aur-select-dropdown/types';
import { ExtendedTreeItemProps } from '../components/eca-doc-list/eca-save-search/EcaSaveSearch';
import {
  SavedSearchEntity,
  SavedSearchRes,
} from '../components/eca/SavedSearchPanel';
import {
  ALPHANUMERIC_REGEX,
  BLANK,
  ID_SUFFIX,
  ONLY_DATE_SHOW_FORMAT,
  QUERY_PREFIX,
  SCOPE_TYPE,
  TAG_NAME_REGEX,
  COUNTRY_CODES,
  HOST_URLS,
} from '../constants/constants';
import { CustodianModal } from '../hooks/index-search/useCustodians';
import { UserData } from '../types/concierge-reports/conciergeReportsType';
import { IReceivedDate, TreeViewRowData } from './inTakeAuditReportUtils';

dayjs.extend(utc);

export type QueryValidationType =
  | 'UNSUPPORTED'
  | 'LOWERCASE_OPERATORS'
  | 'PROXIMITY_W'
  | 'PROXIMITY_P';

export interface QueryValidationResult {
  isValid: boolean;
  errors: QueryValidationType[];
}

type LowercaseOperatorIssueType =
  | 'BOOLEAN_OPERATOR' // AND/OR/NOT incorrect case
  | 'W_PROXIMITY' // w/N
  | 'PRE_PROXIMITY'; // pre/N any non-full-uppercase

export interface LowercaseOperatorDetectionResult {
  hasIssues: boolean;
  issues: LowercaseOperatorIssueType[];
  matches: {
    booleanOperators: string[];
    wProximity: string[];
    preProximity: string[];
  };
}

const BOOLEAN_OPERATOR_INCORRECT_CASE =
  /\b(?!(?:AND|OR|NOT)\b)(?:[Aa][Nn][Dd]|[Oo][Rr]|[Nn][Oo][Tt])\b/g;
const LOWERCASE_W_PROXIMITY = /\bw\/\d+\b/g;
const PRE_INCORRECT_CASE_PROXIMITY =
  /\b(?!(?:PRE\/\d+))(?:[Pp][Rr][Ee]\/\d+)\b/g;

/**
 * Checks if a given value is a valid email address.
 * @param email - The email address to validate.
 * @returns True if the email is valid, otherwise false.
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

/**
 * Validates if the input is a valid mobile number.
 * @param mobileNumber - The mobile number string to validate.
 * @returns True if the mobile number is valid, otherwise false.
 */
export const isValidMobileNumber = (mobileNumber: string): boolean => {
  // Regular expression for validating mobile numbers
  const mobileNumberRegex =
    /^(\+?\d{1,4}[\s-]?)?\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{4}$/;

  return mobileNumberRegex.test(mobileNumber);
};

/**
 * Converts a hex color code to an rgba color code with specified alpha.
 * @param hexaCode - The hex color code (e.g., "#ff0000" or "ff0000").
 * @param Opacity - The alpha value for opacity (between 0 and 1).
 * @returns The rgba color code.
 */

export const hexToRgba = (hexaCode: string, Opacity: number): string => {
  const hex = hexaCode.replace(/^#/, '');

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${Opacity})`;
};

export const replaceLocalePlaceholders = (
  pattern: string,
  replacements: { [key: string]: string }
): string => {
  // Regular expression to match placeholders like {name}
  const placeholderRegex = /{(\w+)}/g;
  const stringToReturn = t(pattern);
  // Replace each placeholder with the corresponding value from the object
  return stringToReturn
    ? stringToReturn.replace(placeholderRegex, (match, key) => {
      // Check if the key exists in the replacements object
      if (key in replacements) {
        return replacements[key];
      } else {
        // Return the original placeholder if the key is not found
        return match;
      }
    })
    : '';
};

// Helper function to extract last name from name field
export const getLastName = (name: string) => {
  const parts = name.split(','); // Assuming format is "LastName, FirstName"
  return parts.length > 1 ? parts[0].trim() : name.trim();
};

export const colorArray = [
  'purple-dark',
  'purple-light',
  'teal-dark',
  'teal-light',
  'green-dark',
  'green-light',
  'lightgreen-dark',
  'lightgreen-light',
  'orange-dark',
  'orange-light',
  'blue-dark',
  'blue-light',
];

// helper function - generate random color for user profile icon
export const getRandomColor = (): string => {
  const randomIndex = Math.floor(generateRandomNumber() * colorArray.length);
  return colorArray[randomIndex];
};

// check string array equality
export const arraysEqual = (
  stringArray1: string[],
  stringArray2: string[]
): boolean => {
  if (stringArray1.length !== stringArray2.length) return false;

  const sortedArr1 = stringArray1.slice().sort();
  const sortedArr2 = stringArray2.slice().sort();

  for (let index = 0; index < sortedArr1.length; index++) {
    if (sortedArr1[index] !== sortedArr2[index]) return false;
  }
  return true;
};

export const isValidDate = (dateString: string): boolean => {
  // Check if the date string matches the format YYYY/MM/DD using a regular expression
  const regex = /^\d{4}\/\d{2}\/\d{2}$/;
  if (!regex.test(dateString)) {
    return false;
  }

  // Split the date string into components
  const [year, month, day] = dateString.split('/').map(Number);

  // Create a Date object from the components
  const date = new Date(year, month - 1, day);

  // Check if the date components match the original input
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

interface DateObj {
  year: string;
  month: string;
  day: string;
}

export const isStartDateSmaller = (
  startDate: DateObj,
  endDate: DateObj
): boolean => {
  const start = new Date(
    parseInt(startDate.year, 10),
    parseInt(startDate.month, 10) - 1,
    parseInt(startDate.day, 10)
  );
  const end = new Date(
    parseInt(endDate.year, 10),
    parseInt(endDate.month, 10) - 1,
    parseInt(endDate.day, 10)
  );

  return start <= end;
};

export const formateNumber = (
  number: number,
  config?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    locale?: string;
  } & Intl.NumberFormatOptions
) => {
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    locale = 'en-US',
  } = config ?? {};
  return number.toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
    ...config
  });
};

export const generateNOpenPDF = (
  imgData: string,
  canvas: HTMLCanvasElement,
  pdfName = 'report.pdf'
) => {
  // Create a new jsPDF instance
  const pdf = new jsPDF();

  // Get the PDF page size (A4 by default)
  const pageWidth = pdf.internal.pageSize.getWidth();

  // Resize the image to fit the PDF page (keeping aspect ratio)
  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * pageWidth) / canvas.width;

  pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);

  pdf.save(pdfName);
};

export const getArrayOfUserIds = (users: ISelectItem[], key: string) => {
  return users.map((item) => item[key]);
};

export const getArrayOfUserIdsList = (users: UserData[]) => {
  return users.filter((user) => !user.isOwner).map((item) => item.userId);
};

export const removeLeadingSlash = (str: string): string => {
  if (str && str.startsWith('/')) {
    return str.slice(1); // Removes the first character
  }
  return str; // Returns the original string if it doesn't start with '/'
};
export interface IArrayData {
  branches?: string[];
  departments?: string[];
  entities?: string[];
  search?: string;
  custodians?: string[];
  productionNames?: string[];
  receivedDate?: IReceivedDate;
  itemCategories?: string[];
  [key: string]: string | string[] | IReceivedDate | undefined;
}
export const removeEmptyArrays = (obj: IArrayData) => {
  for (const key in obj) {
    const value = obj[key];
    if (Array.isArray(value)) {
      if (value.length === 0) {
        delete obj[key];
      }
    } else if (value !== null && typeof value === 'object') {
      removeEmptyArrays(value);
    }
  }
  return obj;
};

export const moveParentDataToChildren = (
  data: TreeViewRowData[],
  expandedRowId: number
) => {
  return data.map((row) => {
    if (row.id === expandedRowId && row.children) {
      const children = row.children;
      if (children.length > 0) {
        const propertiesToShift = Object.keys(row).filter(
          (key) =>
            key !== 'id' &&
            key !== 'receivedDate' &&
            key !== 'children' &&
            key !== 'originalData'
        );

        const lastChild = children[children.length - 1];

        propertiesToShift.forEach((key) => {
          const value = lastChild[key];
          lastChild[key] = row[key];
          row[key] = value;
        });
      }
    }
    return row;
  });
};

// Helper function to restore original data on collapse for tree view DataGrid
export const restoreOriginalData = (row: TreeViewRowData) => {
  if (row.originalData) {
    return { ...row.originalData, isExpanded: false };
  }
  return row;
};
export const convertBinaryToPDF = async (
  binaryData: string
): Promise<string> => {
  const binaryString = atob(binaryData); // Convert base64 to binary string
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
};

export const isValidGuid = (guid: string): boolean => {
  // Regex for validating a GUID (format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
  const guidPattern =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return guidPattern.test(guid);
};

export const openLinkInNewTab = (url: string) => () =>
  window.open(url, t('common.targetBlank'));

/**
 * Download file
 * @param data
 * @param fileName
 */
export const downloadFile = (data: Blob, fileName: string) => {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(data);
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(link.href);
};

/**
 * Print file
 * @param data
 */
export const printFile = (data: Blob) => {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = window.URL.createObjectURL(data);
  document.body.appendChild(iframe);

  iframe.onload = () => {
    iframe.contentWindow?.print();
  };
};

/**
 * To get file name form headers
 * @param contentDisposition
 * @returns
 */
export const getFilenameFromHeaders = (contentDisposition: string) => {
  // Check for `filename*` (priority)
  const filenameStarMatch = contentDisposition?.match(/filename\*=UTF-8''(.+)/);
  if (filenameStarMatch) {
    return decodeURIComponent(filenameStarMatch[1]);
  }

  // filename="abc.xlsx" OR filename=abc.xlsx
  const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
  if (filenameMatch) {
    return filenameMatch[1];
  }

  // If no filename found
  return null;
};

export const getProjectReportFileName = (
  projectName: string | null | undefined,
  chartName: string
) => {
  return `${projectName ?? ''}_${chartName}_${dayjs().format('YYYYMMDDHHmmss')}`;
};

export const downloadResponseAsCSV = (
  result: AxiosResponse,
  fileName: string
) => {
  const blob = new Blob([result?.data], { type: 'text/csv' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', `${fileName}.csv`);
  document.body.appendChild(link);
  link.click();
  if (link.parentNode) {
    link.parentNode.removeChild(link);
  }
  URL.revokeObjectURL(link.href);
};

export const downloadResponseAsCSVInBlob = (
  result: AxiosResponse<Blob>,
  fileName: string
) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(result as unknown as Blob);
  link.setAttribute('download', `${fileName}.csv`);
  document.body.appendChild(link);
  link.click();
  if (link.parentNode) {
    link.parentNode.removeChild(link);
  }
  URL.revokeObjectURL(link.href);
};

export const downloadResponseAsXLSX = (
  result: AxiosResponse,
  fileName: string,
  appendFileExtension = true
) => {
  const blob = new Blob([result?.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', appendFileExtension ? `${fileName}.xlsx` : fileName);
  document.body.appendChild(link);
  link.click();
  if (link.parentNode) {
    link.parentNode.removeChild(link);
  }
  URL.revokeObjectURL(link.href);
};

export const getCurrencySymbol = (currencyCode: string) => {
  const format = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  });
  return (
    format.formatToParts(12345.67).find((part) => part.type === 'currency')
      ?.value ?? ''
  );
};

export const isValidFileName = (fileName: string) => {
  const fileNameRegex = /^[A-Za-z0-9._\-~#+@]+$/;
  return fileNameRegex.test(fileName);
};

const letterToColorMap = {
  'A,M,Y': '#9F75DF', // Dark Purple
  'B,N,Z': '#29D8E1', // Dark Teal
  'C,O': '#2DCB92', // Dark Green
  'D,P': '#D0DB57', // Dark Light Green
  'E,Q': '#FF9E2C', // Dark Orange
  'F,R': '#6E87FF', // Dark Blue
  'G,S': '#C4AAEB', // Light Purple
  'H,T': '#7AE7EC', // Light Teal
  'I,U': '#7DDFBB', // Light Green
  'J,V': '#E2E997', // Light Light Green
  'K,W': '#FFC37D', // Light Orange
  'L,X': '#A5C1FF', // Light Blue
};
export const getColorByFirstName = (firstName: string) => {
  const firstLetter = firstName.charAt(0).toUpperCase();
  // Find the color by checking which group the letter belongs to
  for (const [letters, color] of Object.entries(letterToColorMap)) {
    if (letters.includes(firstLetter)) {
      return color;
    }
  }
  return '#9F75DF'; // Default color if no match
};

/**
 * Get file extension from file name
 * @param fileName
 */
export const getFileExtension = (fileName: string) =>
  fileName.split('.').slice(-1)[0];

// format number 1000000 to 1,234,567
export const formatNumberWithComma = (
  n?: number | string,
  minDecimalPlaces?: number,
  maxDecimalPlaces?: number
) => {
  if (n) {
    const options: Intl.NumberFormatOptions = {};

    if (minDecimalPlaces !== undefined) {
      options.minimumFractionDigits = minDecimalPlaces;
    }

    if (maxDecimalPlaces !== undefined) {
      options.maximumFractionDigits = maxDecimalPlaces;
    }
    return new Intl.NumberFormat('en-US', options).format(Number(n));
  } else if (Number(n) == 0) {
    return 0;
  } else {
    return '';
  }
};
export const formatNumberWithCommaDash = (n?: number | string) => {
  if (n === null || n === undefined || n === '') {
    return ' --- ';
  }

  const numberValue = Number(n);
  if (numberValue === 0) {
    return '0';
  }

  if (!isNaN(numberValue)) {
    return new Intl.NumberFormat('en-US').format(numberValue);
  }

  return ' --- ';
};

export const getFirstFocusableElement = (
  container: HTMLElement | null
): HTMLElement | null => {
  if (!container) return null;

  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])', // Includes Box with tabIndex="0"
  ];
  const focusableElements = container.querySelectorAll<HTMLElement>(
    focusableSelectors.join(',')
  );
  return focusableElements.length > 0 ? focusableElements[0] : null;
};

export const getCardSize = (
  containerWidth: number,
  cardMinW: number,
  gapX: number
) => {
  if (containerWidth <= cardMinW) return containerWidth;
  const minCount = Math.floor(containerWidth / cardMinW);
  if (minCount < 1) return containerWidth;
  const minExtraSpace = (minCount - 1) * gapX;

  const minTotalSpace = minCount * cardMinW + minExtraSpace;
  if (minTotalSpace <= containerWidth) {
    return (containerWidth - minExtraSpace) / minCount;
  } else {
    return (containerWidth - minExtraSpace) / (minCount - 1);
  }

  return 0;
};

export const isEmpty = (value: unknown) => {
  if (value instanceof Set || value instanceof Map) {
    return value.size === 0;
  }

  return (
    value === undefined ||
    value === null ||
    Number.isNaN(value) ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && Object.keys(value).length === 0) ||
    (typeof value === 'string' && value.trim().length === 0)
  );
};

export const isEmptyInstance = (
  value: unknown
): value is
  | undefined
  | null
  | number
  | string
  | unknown[]
  | Record<PropertyKey, unknown> => {
  return isEmpty(value);
};

export const buildDtSearchQuery = (
  dateFilterType: string | undefined,
  startDate: string,
  endDate: string
) => {
  switch (dateFilterType) {
    case 'Is':
      return `FamilyDate=(${startDate})`;
    case 'Is before':
      return `FamilyDate:[<${startDate}]`;
    case 'Is before or on':
      return `FamilyDate:[<${startDate}]`;
    case 'Is after':
      return `FamilyDate:[>${startDate}]`;
    case 'Is after or on':
      return `FamilyDate:[>${startDate}]`;
    case 'Between':
      return `FamilyDate:[${startDate} TO ${endDate}]`;
    default:
      return '';
  }
};

export const generateCustodianQuery = (
  selectedCustodians: Array<CustodianModal>,
  queryConditionType: string
): string => {
  let queryString = '';
  selectedCustodians.map((selectedCustodian, i) => {
    queryString += `${selectedCustodian.name}${i !== selectedCustodians.length - 1 ? ' ' + queryConditionType + ' ' : ''}`;
  });
  const query = QUERY_PREFIX.custodian + ':(' + queryString + ')';
  return getQueryForBlank(
    query,
    selectedCustodians,
    QUERY_PREFIX.custodian,
    queryConditionType
  );
};

export const getQueryForBlank = (
  text: string,
  selectedData: any[],
  selectedType: string,
  conditionType = 'OR'
) => {
  const newSelectedData = selectedData.map((str) =>
    str.name ? str.name.toLowerCase() : str.documentName.toLowerCase()
  );
  return newSelectedData.includes(BLANK)
    ? `(NOT ${selectedType}=(*)) ${conditionType} ${text}`
    : text;
}; // Remove this PR commit once PLS changes completed for Blank type

// Validation function to check for disallowed special characters (except "_" and  "-")
export const validateAllowedSpecialCharacters = (input: string) => {
  const disallowedSpecialCharacters = /[^a-zA-Z0-9 _-]/; // Match any character that is not alphanumeric, underscore, hyphen or space
  return !disallowedSpecialCharacters.test(input);
};

// allow alphanumeric characters, spaces, hyphens, and underscores
export const isValidName = (name: string) => {
  const regex = /^[a-zA-Z0-9 _-]+$/;
  return regex.test(name);
};
type QueryScanContext = {
  depth: number;
  inQuotes: boolean;
};

const advanceQueryScan = (s: string, i: number, ctx: QueryScanContext): number => {
  const c = s[i];

  if (ctx.inQuotes) {
    if (c === '"') ctx.inQuotes = false;
    return i + 1;
  }

  if (c === '"') {
    ctx.inQuotes = true;
    return i + 1;
  }
  if (c === '(') {
    ctx.depth++;
    return i + 1;
  }
  if (c === ')') {
    ctx.depth = Math.max(0, ctx.depth - 1);
    return i + 1;
  }

  return i + 1;
};


const splitTopLevelByOR = (s: string): string[] => {
  const parts: string[] = [];
  let buf = '';
  let i = 0;
  const ctx: QueryScanContext = { depth: 0, inQuotes: false };

  while (i < s.length) {
    if (isAtTopLevel(ctx) && matchWordAt(s, i, 'OR', ctx.inQuotes)) {
      parts.push(buf.trim());
      buf = '';
      i += 2;
      while (i < s.length && /\s/.test(s[i])) i++;
      continue;
    }

    buf += s[i];
    i = advanceQueryScan(s, i, ctx);
  }

  if (buf.trim()) parts.push(buf.trim());
  return parts.length ? parts : [s.trim()];
};

const wrapTopLevelOrOperands = (s: string): string => {
  const parts = splitTopLevelByOR(s);
  if (parts.length <= 1) return s;

  return parts
    .map((part) => {
      const p = part.trim();
      return isFullyWrappedByParens(p) ? p : `(${p})`;
    })
    .join(' OR ');
};

const isAtTopLevel = (ctx: QueryScanContext) => ctx.depth === 0 && !ctx.inQuotes;
/**
 * Wrap query text before run adhoc search in ECA
 * @param text query text
 * @returns
 */
export const wrapQueryBeforeSymbol = (text: string): string => {
  if (!text) return '';

  // === 1. Wrap field names before :, =, contains with <>
  const fieldPattern =
    /\b(?!OR\b|AND\b|NOT\b)([a-z0-9_\-.*~?=]+)(?=\s*(?::(?!\d{2}\b)|contains|=\s*\())/gi;
  let updatedText = text.replace(fieldPattern, '<$1>');

  // === 2. Proximity wrapping ===
  updatedText = wrapTopLevelProximity(updatedText);

  // === 3. Mixed AND/OR grouping ===
  //const hasAND = /\bAND\b/i.test(updatedText);
  //const hasOR = /\bOR\b/i.test(updatedText);

  const hasAND = hasTopLevelAND(updatedText);
  const hasOR = hasTopLevelOR(updatedText);

  // Only process grouping if there's top-level mixing
  //if (!hasAND || !hasOR) return updatedText;
  if (!hasAND || !hasOR) {
    // Optional: wrap top-level OR operands when there is no top-level AND
    if (hasOR) {
      return wrapTopLevelOrOperands(updatedText);
    }
    return updatedText;
  }

  const hasMixedTopLevelAndOr = (() => {
    let depth = 0;
    let hasTopLevelAND = false;
    let hasTopLevelOR = false;

    for (let i = 0; i < updatedText.length; i++) {
      const c = updatedText[i];
      if (c === '(') depth++;
      else if (c === ')') depth = Math.max(0, depth - 1);
      else if (depth === 0) {
        if (matchWordAt(updatedText, i, 'AND')) hasTopLevelAND = true;
        if (matchWordAt(updatedText, i, 'OR')) hasTopLevelOR = true;
      }
      if (hasTopLevelAND && hasTopLevelOR) return true;
    }
    return false;
  })();

  if (!hasMixedTopLevelAndOr) return updatedText;

  const parts = splitTopLevelByAND(updatedText);
  const grouped = parts.map((part) => {
    const p = part.trim();
    if (hasTopLevelOR(p) && !isFullyWrappedByParens(p)) {
      return `(${p})`;
    }
    return p;
  });

  return grouped.join(' AND ');
};

// ========== Proximity Support ==========
const wrapTopLevelProximity = (s: string): string => {
  const getNextToken = (start: number): [number, number] | null => {
    while (start < s.length && /\s/.test(s[start])) start++;

    if (s[start] === '(') {
      let depth = 1;
      for (let i = start + 1; i < s.length; i++) {
        if (s[i] === '(') depth++;
        else if (s[i] === ')') depth--;
        if (depth === 0) return [start, i + 1];
      }
      return null;
    }

    const tokenMatch = s
      .slice(start)
      .match(/^<[^>]+>|"[^"]+"|[a-z0-9_\-.*~?]+/i);
    if (tokenMatch) {
      return [start, start + tokenMatch[0].length];
    }

    return null;
  };

  let i = 0;
  let depth = 0;
  const wraps: Array<[number, number]> = [];

  while (i < s.length) {
    const c = s[i];
    if (c === '(') {
      depth++;
      i++;
      continue;
    }
    if (c === ')') {
      depth = Math.max(0, depth - 1);
      i++;
      continue;
    }

    if (depth > 0) {
      i++;
      continue;
    }

    const left = getNextToken(i);
    if (!left) break;

    const [lStart, lEnd] = left;
    i = lEnd;

    while (i < s.length && /\s/.test(s[i])) i++;

    const proxMatch = s.slice(i).match(/^(W|NEAR|PRE)\/\d+/i);
    if (!proxMatch) continue;

    const proxEnd = i + proxMatch[0].length;
    i = proxEnd;

    while (i < s.length && /\s/.test(s[i])) i++;

    const right = getNextToken(i);
    if (!right) continue;

    const [, rEnd] = right;
    const segment = s.slice(lStart, rEnd).trim();

    const alreadyWrapped =
      segment.startsWith('(') &&
      segment.endsWith(')') &&
      isFullyWrappedByParens(segment);

    if (!alreadyWrapped) {
      wraps.push([lStart, rEnd]);
    }

    i = rEnd;
  }

  if (!wraps.length) return s;

  // Apply wraps from right to left
  const out = s;
  // for (let k = wraps.length - 1; k >= 0; k--) {
  //   const [a, b] = wraps[k];
  //   out = `${out.slice(0, a)}(${out.slice(a, b)})${out.slice(b)}`;
  // }

  return out;
};

// ========== Mixed AND/OR Grouping Support ==========
const isFullyWrappedByParens = (s: string): boolean => {
  s = s.trim();
  if (!s.startsWith('(') || !s.endsWith(')')) return false;

  let depth = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '(') depth++;
    else if (c === ')') {
      depth--;
      if (depth === 0 && i < s.length - 1) return false;
    }
  }

  return depth === 0;
};

const isWordBoundary = (ch: string | undefined) =>
  ch == null || /\s|\(|\)|\[|\]|{|}|,/.test(ch);
/*
const matchWordAt = (s: string, i: number, word: 'AND' | 'OR') => {
  return (
    s.slice(i, i + word.length).toUpperCase() === word &&
    isWordBoundary(s[i - 1]) &&
    isWordBoundary(s[i + word.length])
  );
};*/
const matchWordAt = (
  s: string,
  i: number,
  word: 'AND' | 'OR',
  inQuotes = false
) => {
  if (inQuotes) return false;

  return (
    s.slice(i, i + word.length).toUpperCase() === word &&
    isWordBoundary(s[i - 1]) &&
    isWordBoundary(s[i + word.length])
  );
};
/*
const splitTopLevelByAND = (s: string): string[] => {
  const parts: string[] = [];
  let depth = 0,
    buf = '',
    i = 0;

  while (i < s.length) {
    const c = s[i];

    if (c === '(') {
      depth++;
      buf += c;
      i++;
      continue;
    }
    if (c === ')') {
      depth = Math.max(0, depth - 1);
      buf += c;
      i++;
      continue;
    }

    if (depth === 0 && matchWordAt(s, i, 'AND')) {
      parts.push(buf.trim());
      buf = '';
      i += 3;
      while (i < s.length && /\s/.test(s[i])) i++;
      continue;
    }

    buf += c;
    i++;
  }

  if (buf.trim()) parts.push(buf.trim());
  return parts.length ? parts : [s.trim()];
};

const hasTopLevelOR = (s: string): boolean => {
  let depth = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];

    if (c === '(') depth++;
    else if (c === ')') depth = Math.max(0, depth - 1);
    else if (depth === 0 && matchWordAt(s, i, 'OR')) return true;
  }

  return false;
};*/

const splitTopLevelByAND = (s: string): string[] => {
  const parts: string[] = [];
  let buf = '';
  let i = 0;
  const ctx: QueryScanContext = { depth: 0, inQuotes: false };

  while (i < s.length) {
    if (isAtTopLevel(ctx) && matchWordAt(s, i, 'AND', ctx.inQuotes)) {
      parts.push(buf.trim());
      buf = '';
      i += 3;
      while (i < s.length && /\s/.test(s[i])) i++;
      continue;
    }

    buf += s[i];
    i = advanceQueryScan(s, i, ctx);
  }

  if (buf.trim()) parts.push(buf.trim());
  return parts.length ? parts : [s.trim()];
};

const hasTopLevelOR = (s: string): boolean => {
  let i = 0;
  const ctx: QueryScanContext = { depth: 0, inQuotes: false };

  while (i < s.length) {
    if (isAtTopLevel(ctx) && matchWordAt(s, i, 'OR', ctx.inQuotes)) return true;
    i = advanceQueryScan(s, i, ctx);
  }

  return false;
};

const hasTopLevelAND = (s: string): boolean => {
  let i = 0;
  const ctx: QueryScanContext = { depth: 0, inQuotes: false };

  while (i < s.length) {
    if (isAtTopLevel(ctx) && matchWordAt(s, i, 'AND', ctx.inQuotes)) return true;
    i = advanceQueryScan(s, i, ctx);
  }

  return false;
};
/**
 * Unwrap query text before run adhoc search in ECA
 * @param text query text
 * @returns
 */
export const unwrapQueryText = (text: string) => {
  const pattern = /<([a-z0-9_\-.]+)>/gi;
  return text?.replace(pattern, '$1');
};

/**
 * Number Formatter
 * @param {number} number - decimal, whole, ...
 * @param {Intl.NumberFormatOptions} [options] - Formatting options from Intl.NumberFormat.
 * @returns {string} The formatted number string.
 */
export const numberFormatter = (
  number: number,
  options: Intl.NumberFormatOptions = {}
) => {
  return new Intl.NumberFormat(undefined, options).format(number);
};

/**
 * Get Country Name based on country code as per ISO standard
 * @param {string} string - country code should be of ISO 3166-1 alpha-2
 * @param {string} string - defaultLocale by default is set to "en"
 * @param {string[]} string[] - additionalLocales if required
 * @param {Intl.DisplayNamesOptions} [options] - options for Intl.DisplayNamesOptions.
 * @returns {string} The Country full name by default unless providing additional params.
 */
export const getCountryName = (
  countryCode = '',
  options?: Intl.DisplayNamesOptions,
  defaultLocale = 'en',
  additionalLocales?: string[]
): string => {
  countryCode = countryCode.toUpperCase();

  //Validates and returns empty string if the county code length is greater than 2. [As per ISO 3166-1 alpha-2]
  if (countryCode.length > 2) {
    console.error('Invalid country code');
    return '';
  }

  try {
    const countryFullName = new Intl.DisplayNames(
      [defaultLocale, ...(additionalLocales ?? [])],
      {
        type: 'region',
        fallback: 'code', //returns the input countryCode if the invalid country code is provided
        ...options,
      }
    ).of(countryCode);

    //Checks country code and returns boolean value whether if it's valid or not.
    const isValidCountryCode = countryFullName !== countryCode;

    return countryFullName && isValidCountryCode ? countryFullName : '';
  } catch (error) {
    //catches error while it has invalid country code for ex : "T:", "A("... a code containing invalid char.
    console.error(error);
    return '';
  }
};

export const removeOuterBrackets = (str: string): string => {
  let startCount = 0;
  let endCount = 0;

  // Count leading '('
  while (startCount < str.length && str[startCount] === '(') {
    startCount++;
  }

  // Count trailing ')'
  while (endCount < str.length && str[str.length - 1 - endCount] === ')') {
    endCount++;
  }
  // Remove N outermost parens where N = min(startCount, endCount)
  const removeCount = Math.min(startCount, endCount);
  return str.slice(removeCount, str.length - removeCount);
};

export const formatFileSize = (fileSize: number): string => {
  if (Math.abs(fileSize) < 0.0001) {
    return '> 0 GB';
  }

  let result = Number(fileSize).toFixed(3);

  // Trim trailing zeros and decimal point manually
  while (result.endsWith('0')) {
    result = result.slice(0, -1);
  }
  if (result.endsWith('.')) {
    result = result.slice(0, -1);
  }

  return `${result} GB`;
};

/**
 * Function to get scope label from scope DT-SYNTAX QUERY
 * @param str
 * @returns
 */
export const getLabelFromScopeQuery = (str: string) => {
  const result = RegExp(/:\(([^)]+)\)/).exec(str);
  if (result) {
    return result[1];
  }
  return str;
};

/**
 * Function to create the dt-syntax query using scope object details
 * @param scopeObj
 * @returns
 */
export const createQueryUsingScopeObj = (scopeObj: {
  scopeType: string;
  scopeId: string;
  scopeName: string;
}) => {
  if (scopeObj?.scopeType === SCOPE_TYPE.LOAD) {
    return ` AND ${QUERY_PREFIX.load}=(${scopeObj?.scopeId})`;
  } else if (scopeObj?.scopeType === SCOPE_TYPE.TAGS) {
    return ` AND ${scopeObj?.scopeName}`;
  } else if (scopeObj?.scopeType === SCOPE_TYPE.SAVED_SEARCH) {
    return ` AND ${QUERY_PREFIX.savedSearch}:(${scopeObj?.scopeId})`;
  }
  return '';
};

export const escapeAllCharacters = (input: string): string => {
  // eslint-disable-next-line no-useless-escape
  return input.replace(/[\.\"]/g, '\\$&');
};

export const unescapeString = (input: string): string => {
  // eslint-disable-next-line no-useless-escape
  return input.replace(/\\([\.\"])/g, '$1');
};

export const isValidJsonObject = (jsonStr: string): boolean => {
  try {
    const parsed = JSON.parse(jsonStr);
    return (
      typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
    );
  } catch (e) {
    return false;
  }
};

export const isValidTagName = (name: string): boolean => {
  const isValid =
    name.length > 0 &&
    name.length <= 50 &&
    TAG_NAME_REGEX.test(name) &&
    ALPHANUMERIC_REGEX.test(name);
  return isValid;
};

export const extractSavedSearchId = (id: unknown): string => {
  return String(id).split(ID_SUFFIX.SAVED_SEARCH)[0];
};

export const extractSelectedNodeId = (id: unknown): string => {
  return String(id).split('.')[0];
};

export const waitBeforeRetry = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const donutColor = (percentage: number) => {
  return percentage >= 90
    ? '#ff7a7a'
    : percentage >= 80
      ? '#ffc37d'
      : percentage >= 70
        ? '#FFF187'
        : '#7DDFBB';
};

export const getAlertHitValue = (percentage: number) => {
  return percentage >= 90 ? 90 : percentage >= 80 ? 80 : 70;
};

const currencySymbols = {
  AUD: 'A$',
  GBP: '£',
  CAD: 'C$',
  CNY: '¥',
  EUR: '€',
  HKD: 'HK$',
  INR: '₹',
  JPY: '¥',
  SGD: 'S$',
  CHF: 'CHF',
  AED: 'د.إ',
  USD: '$',
} as const;

type CurrencyCode = keyof typeof currencySymbols;

export const formatCurrencyAmount = (
  code: string | undefined,
  value: number | string | undefined
): string => {
  if (value == null || !code) return '';
  const symbol = currencySymbols[code as CurrencyCode] || '$';
  // Put symbol after for AED; before for others
  return code === 'AED' ? `${value}${symbol}` : `${symbol}${value}`;
};

export const formatBudgetValue = (value: number): number => {
  if (value === 0) return 0;
  if (value > 0 && value < 1) return parseFloat(value.toFixed(1));

  const rounded = value % 1 === 0 ? value : parseFloat(value.toFixed(1));
  return rounded;
};

/** To findout updated current page for pagination when records are removed from DataGridTable and
 * need to refresh page with latest data without resetting pagination
 */
export const getNextPageAfterDelete = ({
  totalItemsBeforeDelete,
  currentPage,
  pageSize,
  numberOfDeletedItems,
}: {
  totalItemsBeforeDelete: number;
  currentPage: number;
  pageSize: number;
  numberOfDeletedItems: number;
}): number => {
  const newTotalItems = totalItemsBeforeDelete - numberOfDeletedItems;

  // Total number of pages after deletion
  const maxValidPage = Math.max(1, Math.ceil(newTotalItems / pageSize));

  // If currentPage still valid, stay on it, otherwise go to last page
  return currentPage <= maxValidPage ? currentPage : maxValidPage;
};

export const parseQuery = (query: RQBJsonLogic): string => {
  const conditions = [];

  for (const key in query) {
    const value = query[key];

    if (key === 'and' || key === 'or') {
      const clauses = value.map((v: any) => parseCondition(v));
      conditions.push(`(${clauses.join(` ${key.toUpperCase()} `)})`);
    } else {
      conditions.push(parseCondition({ [key]: value }));
    }
  }

  return conditions.join(' AND ');
};

export const parseCondition = (token: { [x: string]: any }): string => {
  if (!token || typeof token !== 'object') return '';

  const operatorKey = Object.keys(token)[0];
  const rawValue = token[operatorKey];

  // Handle NOT
  if (operatorKey === '!') {
    const inner = parseQuery(rawValue); // recursive
    return `NOT (${inner})`;
  }

  // Handle compound logic
  if (operatorKey === 'and' || operatorKey === 'or') {
    const op = operatorKey.toUpperCase();
    const parts = rawValue.map((item: any) => parseCondition(item));
    return `(${parts.join(` ${op} `)})`;
  }

  // Must be a simple condition like in, ==, >, etc.
  if (Array.isArray(rawValue) && rawValue.length === 2) {
    const [first, second] = rawValue;

    let field: string, operand: any;
    if (first?.var) {
      field = first.var;
      operand = second;
    } else if (second?.var) {
      field = second.var;
      operand = first;
    } else {
      return ''; // No valid field found
    }

    switch (operatorKey) {
      case '==':
        return `${field}=(${operand})`;

      case 'in':
        if (Array.isArray(operand)) {
          const isRange = operand.length === 1 && operand[0].includes(' TO ');
          const brackets = isRange ? ['[', ']'] : ['(', ')'];
          return `${field}:${brackets[0]}${operand.join(' OR ')}${brackets[1]}`;
        } else {
          return `${field}:(${operand})`;
        }

      case '!=':
        return `NOT ${field}=(${operand})`;

      case '>':
        return `${field}:[> ${operand}]`;

      case '<':
        return `${field}:[< ${operand}]`;

      case '>=':
        return `${field}:[>= ${operand}]`;

      case '<=':
        return `${field}:[<= ${operand}]`;

      case 'startsWith':
        return `${field}:(${operand}*)`;

      case 'endsWith':
        return `${field}:(*${operand})`;

      default:
        throw new Error(`Unsupported operator: ${operatorKey}`);
    }
  }

  return '';
};

export const buildEventSource = (url: string) => {
  return new EventSource(url, {});
};

export const generateRandomNumber = () => {
  const array = new Uint32Array(1);
  window.crypto.getRandomValues(array);
  return array[0] / (0xffffffff + 1);
};

export const getCurrentDateInUTC = () => dayjs.utc();

export const getSubtractedDateInUTC = (noOfDays: number) =>
  getCurrentDateInUTC().subtract(noOfDays, 'day');

const unsupportedSyntax = [
  {
    pattern: /\bdate\([^()]*\)/gi,
    purpose: 'Date recognition',
  },
  {
    pattern: /\bmail\([^()]*\)/gi,
    purpose: 'Email recognition',
  },
  {
    pattern: /\bcreditcard\([^()]*\)/gi,
    purpose: 'Credit card recognition',
  },
  {
    pattern: /\bxfirstword\b/gi,
    purpose: 'Built-in search word',
  },
  {
    pattern: /\bxlastword\b/gi,
    purpose: 'Built-in search word',
  },
  {
    pattern: /%/g,
    purpose: 'Fuzzy searching',
  },
  {
    pattern: /#\w*/g,
    purpose: 'Phonic searching',
  },
];

export const validateQueryV2 = (query: string): QueryValidationResult => {
  const errors: QueryValidationType[] = [];

  // 1. Unsupported
  const hasUnsupported = unsupportedSyntax.some(({ pattern }) =>
    new RegExp(pattern).test(query)
  );

  if (hasUnsupported) {
    errors.push('UNSUPPORTED');
  }

  // 2. Invalid
  let hasInvalid = false;

  if (/=+/.test(query)) {
    const validAssignmentPattern = /<?[a-z]+>?\s*=\s*\(?[a-z0-9]+\)?/gi;
    if (!validAssignmentPattern.test(query)) {
      hasInvalid = true;
    }
  }

  // UNSUPPORTED add if not found unsupporetd for invali also
  if (hasInvalid && !hasUnsupported) {
    errors.push('UNSUPPORTED');
  }

  // 3. Lowercase operator or proximity issue
  const lowerCaseResult = detectLowercaseOperators(query);

  if (lowerCaseResult.hasIssues) {
    if (lowerCaseResult.matches.booleanOperators.length > 0)
      errors.push('LOWERCASE_OPERATORS');
    if (lowerCaseResult.matches.preProximity.length > 0)
      errors.push('PROXIMITY_P');
    if (lowerCaseResult.matches.wProximity.length > 0)
      errors.push('PROXIMITY_W');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateQuery = (query: string): boolean => {
  const hasUnsupported = unsupportedSyntax.some(({ pattern }) =>
    new RegExp(pattern).test(query)
  );

  if (hasUnsupported) return false;

  if (/=+/.test(query)) {
    const validAssignmentPattern = /<?[a-z]+>?\s*=\s*\(?[a-z0-9]+\)?/gi;
    if (!validAssignmentPattern.test(query)) {
      return false;
    }
  }

  return true;
};

export const detectLowercaseOperators = (
  query: string
): LowercaseOperatorDetectionResult => {
  const booleanMatches = query.match(BOOLEAN_OPERATOR_INCORRECT_CASE) || [];
  const wMatches = query.match(LOWERCASE_W_PROXIMITY) || [];
  const preMatches = query.match(PRE_INCORRECT_CASE_PROXIMITY) || [];

  const issues: LowercaseOperatorIssueType[] = [];
  if (booleanMatches.length > 0) issues.push('BOOLEAN_OPERATOR');
  if (wMatches.length > 0) issues.push('W_PROXIMITY');
  if (preMatches.length > 0) issues.push('PRE_PROXIMITY');

  return {
    hasIssues: issues.length > 0,
    issues,
    matches: {
      booleanOperators: booleanMatches,
      wProximity: wMatches,
      preProximity: preMatches,
    },
  };
};

const sortSavedSearchEntity = (a: SavedSearchEntity, b: SavedSearchEntity) =>
  (a?.entity?.name ?? '').localeCompare(b?.entity?.name ?? '');

const sortSavedSearch = (a: SavedSearchRes, b: SavedSearchRes) =>
  (a?.name ?? '').localeCompare(b?.name ?? '');

export const getSavedSearchResFromEntity = (
  savedSearchEntity: SavedSearchEntity
): SavedSearchRes => {
  const {
    indexName,
    groupId,
    name,
    type,
    parentGroupId,
    savedSearchId,
    isPublic,
    numberOfChild,
    isDefault,
    savedSearchList,
    createdBy,
    createdTime
  } = savedSearchEntity?.entity ?? {};
  return {
    indexName,
    groupId,
    name,
    type,
    parentGroupId,
    savedSearchId,
    isPublic,
    numberOfChild,
    isDefault,
    createdBy,
    createdTime,
    children: [
      ...(savedSearchEntity?.childNodes ?? [])
        .sort(sortSavedSearchEntity)
        .map(getSavedSearchResFromEntity),
      ...(savedSearchList ?? []).sort(sortSavedSearch),
    ],
  };
};

export const getTreeObjectFromSavedSearch = (
  item: SavedSearchRes,
  index: number,
  objectId: string
): TreeViewBaseItem<ExtendedTreeItemProps> => {
  const {
    groupId,
    name,
    type,
    parentGroupId,
    savedSearchId,
    isPublic,
    numberOfChild,
    isDefault,
    children,
    createdBy,
    createdTime
  } = item ?? {};
  let directory_structure_ids = '';

  if (type === 'Group' && isEmpty(parentGroupId)) {
    directory_structure_ids = objectId;
  } else if (!isEmpty(parentGroupId)) {
    if (objectId !== parentGroupId) {
      directory_structure_ids = `${objectId}/${parentGroupId}${type === 'Group' ? '/' + groupId : ''}`;
      directory_structure_ids = uniq(directory_structure_ids.split('/')).join(
        '/'
      );
    } else {
      directory_structure_ids = objectId;
    }
  }

  let fileType = '';
  if (type === 'Group') {
    fileType = 'folder';
  } else {
    fileType = isPublic ? 'public' : 'private';
  }

  return {
    id: type === 'Group' ? (groupId ?? objectId) : (savedSearchId ?? objectId),
    label: name,
    fileType,
    parentGroupId,
    numberOfChild,
    directory_structure_ids,
    isDefault: isDefault,
    createdBy,
    createdDate: createdTime,
    children: numberOfChild
      ? [
        ...(children ?? []).map((item: SavedSearchRes, index: number) =>
          getTreeObjectFromSavedSearch(item, index, directory_structure_ids)
        ),
      ]
      : undefined,
  };
};

export const findTreeNodeById = (
  tree:
    | TreeViewBaseItem<ExtendedTreeItemProps>
    | TreeViewBaseItem<ExtendedTreeItemProps>[],
  idToFind: string
): TreeViewBaseItem<ExtendedTreeItemProps> | undefined => {
  if (Array.isArray(tree)) {
    for (const node of tree) {
      const found = findTreeNodeById(node, idToFind);
      if (found) {
        return found;
      }
    }
  } else if (tree) {
    if (tree.id === idToFind) {
      return tree;
    }

    if (tree.children) {
      const foundInChild = findTreeNodeById(tree.children, idToFind);
      if (foundInChild) {
        return foundInChild;
      }
    }
  }

  return undefined;
};

export const getExpandableIds = (
  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
): string[] => {
  if (!nodes || nodes.length === 0) {
    return [];
  }

  return nodes.reduce<string[]>((ids, node) => {
    const hasChildren = (node?.children ?? []).length > 0;
    const currentId = hasChildren ? node.id : null;

    const childIds = hasChildren ? getExpandableIds(node?.children ?? []) : [];

    return [...ids, ...(currentId ? [currentId] : []), ...childIds];
  }, []);
};

export const getHostCountryCode = (): string => {
  const url = window.location.href;
  const hostname = window.location.hostname;

  // List of known US-specific hostnames
  const usHostnames = [
    'aurora-dev1',
    'aurora-qa1',
    'aurora-uat1',
    'aurora-uat2',
    'localhost',
  ];

  // Check if hostname contains any of the US-specific hostnames
  if (usHostnames.some((part) => hostname.includes(part))) {
    return COUNTRY_CODES.UNITED_STATE;
  }

  // Check if URL starts with https://aurora.consilio.com
  if (url.startsWith('https://aurora.consilio.com')) {
    return COUNTRY_CODES.UNITED_STATE;
  }

  // Check if URL starts with a country code like https://us.aurora.consilio.com
  const countryCodeMatch = hostname.match(
    /^([a-z]{2})\.aurora\.consilio\.com/i
  );
  if (countryCodeMatch) {
    return countryCodeMatch[1].toLowerCase();
  }

  // Default
  return COUNTRY_CODES.UNITED_STATE;
};

export const isLowerEnvironment = (): boolean =>
  window.location.hostname === 'localhost' ||
  Object.values(HOST_URLS).some((hostUrl) =>
    hostUrl.includes(window.location.hostname)
  );

export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  if (i === 0) return `${bytes} ${sizes[i]}`;

  return `${(bytes / Math.pow(k, i)).toFixed(decimals)} ${sizes[i]}`;
};

export interface SavedSearchLastRunCheckDetails {
  applyFilter?: boolean;
  filteredDocCount?: number | null;
  filterSet?: { items?: unknown[] };
  lastRunDetail?: { lastRunTime?: string };
  updatedDetail?: { updatedTime?: string };
}

export const checkSavedSearchLastRun = (
  details: SavedSearchLastRunCheckDetails
): boolean => {
  if (
    details.applyFilter &&
    details.filteredDocCount != null &&
    (details.filterSet?.items?.length ?? 0) > 0
  ) {
    return true;
  }

  const lastRunTime = details.lastRunDetail?.lastRunTime;
  if (lastRunTime && lastRunTime !== '') {
    return isUpdatedAfterRunTime(
      details.updatedDetail?.updatedTime ?? '',
      lastRunTime
    );
  }

  return false;
};

export const isUpdatedAfterRunTime = (
  updateTime: string,
  runTime: string
): boolean => {
  const modifiedDate = new Date(updateTime);
  const lastRunDate = new Date(runTime);
  modifiedDate.setMilliseconds(0);
  lastRunDate.setMilliseconds(0);
  if (modifiedDate > lastRunDate) {
    return false;
  } else {
    return true;
  }
};

// return formatted date in standard UTC formate
export const getUTCDateTimeForDisplay = (
  value?: string | Date | number,
  format = 'YYYY/MM/DD [UTC] HH:mm'
) => {
  if (!value) return 'N/A'; // Handle undefined or invalid dates

  const isTimestamp =
    typeof value === 'number' ||
    (typeof value === 'string' && /^\d+$/.test(value));

  // Timestamp (ms since epoch)
  if (isTimestamp) {
    return dayjs.utc(Number(value)).format(format);
  }

  if (typeof value === 'object' || value.includes('T')) {
    // Full ISO Format
    return dayjs.utc(value).format(format);
  }
  return dayjs(value).format(format);
};

// formate date in local
export const getLocalFormatedDate = (
  value?: string | Date,
  format = ONLY_DATE_SHOW_FORMAT
) => {
  return dayjs(value ?? new Date()).format(format);
};

export const convertDateStringToObject = (
  value: string,
  convertToLocalTime = true
): { year: string; month: string; day: string } => {
  const date: Date = new Date(value);

  // Get the full year from the Date object (e.g., 2024)
  const year: string = convertToLocalTime
    ? date.getFullYear().toString()
    : date.getUTCFullYear().toString();

  // Get the month (0-indexed) and convert it to a string, ensuring it's two digits
  const month: string = convertToLocalTime
    ? String(date.getMonth() + 1).padStart(2, '0')
    : String(date.getUTCMonth() + 1).padStart(2, '0'); // Adding 1 because getMonth() returns a zero-based month

  const day: string = convertToLocalTime
    ? String(date.getDate()).padStart(2, '0')
    : String(date.getUTCDate()).padStart(2, '0');

  return {
    year,
    month,
    day,
  };
};

export const getFileSizeWithUnit = (
  size: number,
  unit: string = t('common.gbUnit')
) => size + ' ' + unit;

export const manipulateProjectCountryCode = (
  hostCountryCode: string,
  projectCountryCode: string
): string => {
  if (projectCountryCode.toLowerCase() == COUNTRY_CODES.GREAT_BRITAIN) {
    return COUNTRY_CODES.UNITED_KINGDOM;
  }

  /**
   * Handle edge case when host country is Hong kong and project hosted in china then
   * we need to allow it and don't show modal warning for this case
   * https://consilio.atlassian.net/browse/ARHD-20754
   */
  if (
    hostCountryCode.toLowerCase() === COUNTRY_CODES.HONG_KONG &&
    projectCountryCode.toLowerCase() === COUNTRY_CODES.CHINA
  ) {
    return COUNTRY_CODES.HONG_KONG;
  }

  return projectCountryCode;
};


export type FileSizeUnit = 'Bytes' | 'KB' | 'MB' | 'GB' | 'TB';

const UNIT_FACTORS: Record<FileSizeUnit, number> = {
  Bytes: 1,
  KB: 1024,
  MB: 1024 ** 2,
  GB: 1024 ** 3,
  TB: 1024 ** 4,
};

export const convertFileSize = (
  value: number,
  from: FileSizeUnit,
  to: FileSizeUnit,
  decimals = 2
): number => {
  const bytes = value * UNIT_FACTORS[from];

  return Number((bytes / UNIT_FACTORS[to]).toFixed(decimals));
};

export const getFileSize = (
  files: File[] | number,
  unit: FileSizeUnit = 'MB',
  decimals = 2
): number => {
  const bytes =
    typeof files === 'number'
      ? files
      : files.reduce((total, file) => total + file.size, 0);
  return Number((bytes / UNIT_FACTORS[unit]).toFixed(decimals));
};

export const scrollToGridView = (
  elementClass: string,
  performDataGridVirtualScroll?: boolean
) => {
  const elementContainer = document.querySelector(
    elementClass.startsWith('#') ? elementClass : `.${elementClass}`
  ) as HTMLElement | null;
  if (!elementContainer) return;

  // Sticky header
  const topNav = document.querySelector('.aur-header');
  // Getting height of top sticky nav header and added 10 margin so some space visible when scroll happen for table
  const topMargin = (topNav?.getBoundingClientRect().height ?? 72) + 10;

  // Grid top is already visible below the sticky header
  if (elementContainer.getBoundingClientRect().top >= topMargin) {
    if (performDataGridVirtualScroll) {
      setTimeout(() => {
        requestAnimationFrame(() => {
          const virtualScrollbar = elementContainer.querySelector(
            '.MuiDataGrid-virtualScroller'
          ) as HTMLElement | null;
          if (virtualScrollbar)
            virtualScrollbar.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
        });
      }, 300);
    }
    return;
  }

  // Added time out to make sure grid updated correclty with data before scroll
  setTimeout(() => {
    requestAnimationFrame(() => {
      const y =
        (elementContainer.getBoundingClientRect().top ?? 0) +
        window.pageYOffset -
        topMargin; // header height + margin

      window.scrollTo({
        top: y,
        behavior: 'smooth',
      });

      if (performDataGridVirtualScroll) {
        const virtualScrollbar = elementContainer.querySelector(
          '.MuiDataGrid-virtualScroller'
        ) as HTMLElement | null;
        if (virtualScrollbar)
          virtualScrollbar.scrollTo({
            top: 0,
          });
      }
    });
  }, 300);
};

export const scrollToAccordion = (
  element: HTMLElement | null,
  delay = 0
) => {
  if (!element) return;

  const topNav = document.querySelector('.aur-header');
  const topMargin = (topNav?.getBoundingClientRect().height ?? 72);

  setTimeout(() => {
    requestAnimationFrame(() => {
      const y =
        element.getBoundingClientRect().top +
        window.pageYOffset -
        topMargin;

      window.scrollTo({
        top: y,
        behavior: 'smooth',
      });
    });
  }, delay);
};
